import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ErrorIcon from '@mui/icons-material/Error'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  Box,
  Checkbox,
  FormControlLabel,
  Link,
  Stack,
  Typography
} from '@mui/material'
import { SignUpBody, signUpBodySchema } from '@shared/dto/auth.dto'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import FundingLogos from '../../components/common/FundingLogos'
import { PasswordInput } from '../../components/forms/PasswordInput'
import { TextInput } from '../../components/forms/TextInput'
import { LoaderButton } from '../../components/LoaderButton'
import { useSignUpMutation } from '../../hooks/useMutations/useAuthMutations'
import { hasCombination, hasMinLength } from '../../utils/validation.utils'

export default function SignUp() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const signUpMutation = useSignUpMutation()
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isValid, isDirty }
  } = useForm<SignUpBody>({
    defaultValues: {
      email: '',
      password: ''
    },
    resolver: standardSchemaResolver(signUpBodySchema),
    mode: 'onBlur'
  })

  const onSubmitRegister = (data: SignUpBody) => {
    signUpMutation.mutate(data, {
      onSuccess: () => {
        navigate(`/sign-up/confirm?email=${email}`)
      },
      onError: (error: any) => {
        if (error?.response?.data?.data?.reason === 'UsernameExistsException') {
          setError('email', {
            type: 'UsernameExistsException',
            message: 'Username exists'
          })
        } else if (error?.response?.data?.data?.reason === 'InvalidEmail') {
          setError('email', {
            type: 'InvalidEmail',
            message: 'Invalid corporate mail'
          })
        } else if (
          error?.response?.data?.data?.reason === 'LimitExceededException'
        ) {
          setError('email', {
            type: 'LimitExceededException',
            message: 'Limit exceeded'
          })
        } else {
          console.error(error)
        }
      }
    })
  }

  const email = watch('email')
  const password = watch('password')
  const isMinLengthValid = hasMinLength(password)
  const isCombinationValid = hasCombination(password)
  const isDisabled =
    !email ||
    !isMinLengthValid ||
    !isCombinationValid ||
    !hasAcceptedTerms ||
    !isDirty ||
    !isValid

  return (
    <Stack
      direction='column'
      spacing='1rem'
      sx={{
        width: '37.875rem',
        minHeight: '37.875rem',
        padding: '2.5rem',
        borderRadius: '1.5rem',
        backgroundColor: 'background.default'
      }}
    >
      <Typography variant='h4'>{t('sign_up.title')}</Typography>
      <Typography variant='h6' color='text.secondary'>
        {t('sign_up.subtitle')}
      </Typography>
      <TextInput
        name='email'
        register={register}
        errors={errors}
        label={t('sign_up.email')}
      />
      <PasswordInput
        name='password'
        register={register}
        errors={errors}
        label={t('sign_up.password')}
      />
      <Box display='flex' alignItems='center' gap='0.5rem'>
        {password.length === 0 ? (
          <InfoOutlinedIcon color='info' />
        ) : isMinLengthValid ? (
          <CheckCircleIcon color='success' />
        ) : (
          <ErrorIcon color='error' />
        )}
        <Typography variant='body1'>{t('password.length')}</Typography>
      </Box>
      <Box display='flex' alignItems='center' gap='0.5rem'>
        {password.length === 0 ? (
          <InfoOutlinedIcon color='info' />
        ) : isCombinationValid ? (
          <CheckCircleIcon color='success' />
        ) : (
          <ErrorIcon color='error' />
        )}
        <Typography variant='body1' color='text.primary'>
          {t('password.combination')}
        </Typography>
      </Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={hasAcceptedTerms}
            onChange={e => setHasAcceptedTerms(e.target.checked)}
          />
        }
        label={
          <Typography>
            {t('sign_up.terms.part1')}{' '}
            <Link href='/terms' target='_blank' rel='noopener'>
              {t('sign_up.terms.link')}
            </Link>{' '}
            {t('sign_up.terms.part2')}
          </Typography>
        }
      />
      <LoaderButton
        label={t('sign_up.button')}
        onClick={handleSubmit(onSubmitRegister)}
        variant='contained'
        loading={signUpMutation.isPending}
        disabled={isDisabled}
        sx={{ textTransform: 'none' }}
        startIcon={<ChevronLeftIcon />}
      />
      <Stack
        direction='row'
        spacing='.5rem'
        alignItems='center'
        marginTop='3rem'
      >
        <Typography variant='subtitle1' color='textSecondary'>
          {t('sign_up.already_registered')}
        </Typography>
        <Typography>
          <Link href='/sign-in'>{t('sign_up.login_link')}</Link>
        </Typography>
      </Stack>
      <FundingLogos size='small' sx={{ mt: '1.5rem' }} />
    </Stack>
  )
}
